import { DevicePermissions } from '@order-management/roles';
import {
  DeviceModel,
  DeviceRegistrationMethod,
  DeviceRegistrationStatus,
  InputType,
  PaginatedAPIResponse,
  PaginatedContentConfig,
  RegistrationFileData,
  RegistrationFileDevicesCriteria,
  RegistrationFileDevicesExpandableTableRow,
  RegistrationFileDevicesTableRow,
  SortDirection,
} from '@order-management/types';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Columns, Element } from 'react-bulma-components';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
  useNavigate,
  useNavigation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import BackButton from '../../shared/back-button/back-button';
import Conditional from '../../shared/conditional/conditional';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import useHttpRequest from '../../shared/hooks/http-request';
import ProtectedPage from '../../shared/protected-page/protected-page';
import { getSearchParamValue } from '../../shared/url';
import BoxLayout from '../../ui/box-layout/box-layout';
import Button from '../../ui/button/button';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import { OnTableChangeData } from '../../ui/table/types';
import { fetchDeviceModels } from '../devices-service';
import RegistrationFileDevicesTable from '../registration-file-devices-table/registration-file-devices-table';
import { fetchRegistrationFile } from '../registration-files-service';

const initialCriteria: RegistrationFileDevicesCriteria = {
  itemsPerPage: 50,
  pageNumber: 1,
  search: '',
  sortBy: '',
  sortDirection: SortDirection.ASC,
};

export const loader: LoaderFunction = async ({ params }) => {
  const id = params.id;

  const response = fetchRegistrationFile(id!, initialCriteria);

  return defer({ response });
};

export function RegistrationFileDetailsPageContent() {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const registrationFile = useAsyncValue() as RegistrationFileData;
  const [filterCriteria, setFilterCriteria] =
    useState<RegistrationFileDevicesCriteria>(initialCriteria);
  const [showRelatedRegisteredDevices, setShowRelatedRegisteredDevices] =
    useState<boolean>(
      getSearchParamValue(searchParams, 'relatedRegisteredDevices') === 'true'
    );
  const { executeHttpRequest, isLoading } = useHttpRequest();
  const [tableData, setTableData] = useState<RegistrationFileDevicesTableRow[]>(
    registrationFile.devices.data
  );
  const [tableConfig, setTableConfig] = useState<PaginatedContentConfig>({
    ...filterCriteria,
    pages: registrationFile.devices.pages,
  });

  const isDataLoading = useMemo(
    () => navigation.state === 'loading',
    [navigation.state]
  );

  const fetchRegistrationFileDevices = useCallback(
    (criteria: RegistrationFileDevicesCriteria) => {
      const registrationFileId = params.id;
      executeHttpRequest({
        asyncFunction: async () => {
          const response = await fetchRegistrationFile(
            registrationFileId!,
            criteria
          );
          setTableData(response.devices.data);
          setTableConfig({ ...criteria, pages: response.devices.pages });
        },
      });
    },
    [executeHttpRequest, params.id]
  );

  const handleOnTableChange = useCallback(
    (
      value: OnTableChangeData<
        RegistrationFileDevicesTableRow,
        RegistrationFileDevicesExpandableTableRow
      >
    ): void => {
      const newCriteria = {
        ...filterCriteria,
        ...value.config,
        sortBy: value.column?.key || filterCriteria.sortBy,
      };

      setFilterCriteria(newCriteria);
      fetchRegistrationFileDevices(newCriteria);
    },
    [fetchRegistrationFileDevices, filterCriteria]
  );

  const handleTableFilterSubmit = useCallback(
    (value: RegistrationFileDevicesCriteria) => {
      const { itemsPerPage, search, sortBy, sortDirection } = filterCriteria;
      const newCriteria: RegistrationFileDevicesCriteria = {
        ...value,
        itemsPerPage,
        search,
        sortBy,
        sortDirection,
        pageNumber: 1,
      };
      setFilterCriteria(newCriteria);
      fetchRegistrationFileDevices(newCriteria);
    },
    [fetchRegistrationFileDevices, filterCriteria]
  );

  const handleShowRelatedRegisteredDevicesClick = useCallback(
    async (e: React.MouseEvent<HTMLElement>) => {
      const newCriteria = {
        ...filterCriteria,
        relatedRegisteredDevices: !showRelatedRegisteredDevices,
      };
      setFilterCriteria(newCriteria);
      fetchRegistrationFileDevices(newCriteria);
      setShowRelatedRegisteredDevices(!showRelatedRegisteredDevices);
      e.currentTarget.blur();
    },
    [fetchRegistrationFileDevices, filterCriteria, showRelatedRegisteredDevices]
  );

  const filtersData = useMemo(() => {
    return {
      serialNumbers: {
        title: 'Serial number',
        appliedCount: filterCriteria.serialNumbers?.length,
        value: filterCriteria.serialNumbers,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'serialNumbers',
          valueKey: 'serialNumbers',
          placeholder: 'Search by Serial number',
        },
      },
      name: {
        title: 'Name',
        appliedCount: filterCriteria.name?.length,
        value: filterCriteria.name,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'name',
          valueKey: 'name',
          placeholder: 'Search by Name',
        },
      },
      type: {
        title: 'Type',
        appliedCount: filterCriteria.type?.length,
        value: filterCriteria.type,
        input: {
          type: InputType.SELECT,
          valueKey: 'type',
          fetchItems: fetchDeviceModels,
          resolveOption: (item: DeviceModel) => ({
            label: item.displayName,
            value: item.displayName,
          }),
        },
      },
      status: {
        title: 'Status',
        appliedCount: filterCriteria.status?.length,
        value: filterCriteria.status,
        input: {
          type: InputType.SELECT,
          valueKey: 'status',
          options: Object.values(DeviceRegistrationStatus).map((status) => ({
            label: status,
            value: status,
          })),
        },
      },
      registrationMethods: {
        title: 'Registration method',
        appliedCount: filterCriteria.registrationMethods?.length,
        value: filterCriteria.registrationMethods,
        input: {
          type: InputType.SELECT,
          valueKey: 'registrationMethods',
          options: Object.values(DeviceRegistrationMethod).map((method) => ({
            label: method,
            value: method,
          })),
        },
      },
    };
  }, [
    filterCriteria.name,
    filterCriteria.registrationMethods,
    filterCriteria.serialNumbers,
    filterCriteria.status,
    filterCriteria.type,
  ]) as FiltersData<RegistrationFileDevicesCriteria>;

  const appliedFilters = useMemo(
    () =>
      Object.entries(filtersData).filter(([_, filter]) => filter.appliedCount),
    [filtersData]
  );

  const emptyContent = useMemo(() => {
    const emptyContentText =
      appliedFilters.length > 0 || tableConfig.search !== ''
        ? 'There are no devices that match the selected criteria'
        : 'No devices have been registered yet';
    return (
      <Element display="flex" justifyContent="center" className="my-2">
        <span>{emptyContentText}</span>
      </Element>
    );
  }, [appliedFilters.length, tableConfig.search]);

  const onRemoveFilters = useCallback(
    (filterDataKeys: (keyof RegistrationFileDevicesCriteria)[]) => {
      const newCriteria = { ...filterCriteria };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue = initialCriteria[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newCriteria[filterDataKey];
        } else {
          newCriteria[filterDataKey] = initialFilterValue as string &
            string[] &
            number;
        }
      }
      handleTableFilterSubmit(newCriteria);
    },
    [filterCriteria, handleTableFilterSubmit]
  );

  const navigateToRegisteredDevices = useCallback(() => {
    navigate('/my-devices/device-registration/registration-files');
  }, [navigate]);

  if (isDataLoading) {
    return (
      <Element display="flex" justifyContent="center">
        <PageSpinner />
      </Element>
    );
  }

  return (
    <BoxLayout
      className="is-min-height-80 mx-10 my-4"
      header={
        <Element className="p-4">
          <BackButton onClick={navigateToRegisteredDevices} />
          <Columns marginless>
            <Columns.Column className="pl-0">
              <h1 className="is-size-3 has-text-weight-light">
                {registrationFile.name} details
              </h1>
            </Columns.Column>
          </Columns>
        </Element>
      }
    >
      <Element display="flex" flexDirection="column" mb={4}>
        <h2 className="is-size-6 has-text-weight-normal">
          Here you can find a list of devices authorized to be registered using
          this file and their status
        </h2>
        <h2 className="is-size-6 has-text-weight-normal">
          Devices that show up as Unregistered* are devices that were previously
          registered using this file but have since been unregistered.
        </h2>
      </Element>

      <Element display="flex" alignItems="center" mb={4}>
        <Button
          type="button"
          color="primary-dark"
          outlined={!showRelatedRegisteredDevices}
          onClick={handleShowRelatedRegisteredDevicesClick}
        >
          <span>Only devices registered using this file</span>
        </Button>
      </Element>

      <Conditional condition={isLoading}>
        <Conditional.True>
          <PageSpinner />
        </Conditional.True>
        <Conditional.False>
          <Element display="flex" className="w-full" alignItems="center" mb={2}>
            <FilterBar
              initialData={initialCriteria}
              data={filterCriteria}
              filtersData={filtersData}
              onFilterSubmit={handleTableFilterSubmit}
              onRemoveFilter={onRemoveFilters}
            />
          </Element>
          <RegistrationFileDevicesTable
            dataSource={tableData}
            config={tableConfig}
            onChange={handleOnTableChange}
            empty={emptyContent}
            filterCriteria={filterCriteria}
            form={{
              enableSearch: false,
            }}
          />
        </Conditional.False>
      </Conditional>
    </BoxLayout>
  );
}

export function RegistrationFileDetailsPage() {
  const { response } = useLoaderData() as {
    response: Promise<PaginatedAPIResponse<RegistrationFileDevicesTableRow>>;
  };

  return (
    <ProtectedPage permissions={DevicePermissions.REGISTERED_DEVICES_READ}>
      <Suspense fallback={<PageSpinner />}>
        <ErrorHandlingAwait resolve={response}>
          <RegistrationFileDetailsPageContent />
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default RegistrationFileDetailsPage;
